import React, { useState } from 'react';
import deleteBooks from './../images/delete-left-line.svg';
import deleteAccount from './../images/delete-left-line.svg';
import closeButton from '../images/close-line-white.svg';
import backIcon from '../images/close-line.svg';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { analytics, auth, db } from '../firebaseConfig';
import { deleteUser, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import TextInput from './TextInput';
import { logEvent } from 'firebase/analytics';

const DeleteAccount = ({
  userData,
  fetchBooks,
  showNotification,
  updateCurrentlyReading,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [deleteModalIsOpen, setDeleteIsOpen] = useState(false);
  const [deletingAllBooks, setDeletingAllBooks] = useState(false);
  function openDeleteModal() {
    setDeleteIsOpen(true);
  }
  function closeDeleteModal() {
    setDeleteIsOpen(false);
  }

  const deleteAllUserBooks = async (notify) => {
    if (!userData.uid) {
      console.error('No user id');
      return false;
    }
    setDeletingAllBooks(true);
    const itemsCollection = collection(db, 'UserBooks');
    const q = query(itemsCollection, where('userID', '==', userData.uid));

    try {
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);

      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      fetchBooks();
      updateCurrentlyReading({ userID: userData.uid }, userData, false);
      if (notify) {
        showNotification(t.allbooksdeleted);
      }
    } catch (error) {
      console.error('Error deleting items: ', error);
    } finally {
      setDeletingAllBooks(false);
      closeDeleteModal();
    }
  };

  const [deleteUserModalIsOpen, setDeleteUserIsOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [email, setEmail] = useState('');

  function openDeleteUserModal() {
    setDeleteUserIsOpen(true);
  }
  function closeDeleteUserModal() {
    setEmail('');
    setDeleteUserIsOpen(false);
  }

  const handleTitleChange = (e) => {
    setEmail(e.target.value);
  };

  const hasEnteredEmail =
    userData?.email?.toLowerCase() === email.toLowerCase();

  const deleteUserAccount = async () => {
    setDeletingUser(true);
    try {
      // Delete all books
      await deleteAllUserBooks();
      // Get the current user
      const user = auth.currentUser;
      if (!user) throw new Error('No user is logged in.');

      // Get the user's UID
      const uid = user.uid;

      // Delete the user document from Firestore
      await deleteDoc(doc(db, 'Users', uid));

      // Delete the user from Firebase Authentication
      await deleteUser(user);

      showNotification(t.accountdeleted);
      logEvent(analytics, 'account_settings_account_delete');
      navigate('/');
    } catch (error) {
      console.error('Error deleting user account:', error);
      if (error.code === 'auth/requires-recent-login') {
        showNotification(t.reauthenticate);
        signOut(auth)
          .then(() => {
            navigate('/');
          })
          .catch((error) => {
            console.error('Navbar error:', error);
          });
      }
    } finally {
      setDeletingUser(false);
    }
  };

  return [
    userData?.admin && (
      <div className="row-item" key="delete-books">
        <img height={24} width={24} alt="Delete books" src={deleteBooks} />
        <div
          className="link"
          onClick={() => openDeleteModal()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              openDeleteModal();
            }
          }}
          tabIndex={0}
        >
          {t.deleteallbooks}
        </div>
      </div>
    ),

    deleteModalIsOpen && (
      <div className="overlay" key="delete-books-modal">
        <div className="buttons-wrapper">
          <div className="buttons-header">
            <span>{t.deleteallbooks}</span>
            <button
              className="btn-icon"
              onClick={closeDeleteModal}
              disabled={deletingAllBooks}
            >
              <img
                className="icon"
                src={closeButton}
                width={24}
                height={24}
                alt="Close icon"
              />
            </button>
          </div>
          <div className="separator"></div>
          <div className="buttons">
            <button
              className="btn btn-secondary btn-danger"
              onClick={() => deleteAllUserBooks(true)}
              disabled={deletingAllBooks}
            >
              {t.deleteallbooks}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => closeDeleteModal()}
              disabled={deletingAllBooks}
            >
              {t.cancel}
            </button>
          </div>
        </div>
      </div>
    ),

    <div className="row-item" key="delete-account">
      <img height={24} width={24} alt={t.deleteaccount} src={deleteAccount} />
      <div
        className="link"
        onClick={() => openDeleteUserModal()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            openDeleteUserModal();
          }
        }}
        tabIndex={0}
      >
        {t.deleteaccount}
      </div>
    </div>,

    deleteUserModalIsOpen && (
      <div
        className="dialog-content fullscreen-overlay"
        key="delete-account-ocerlay"
      >
        <div className="fullscreen-container" tabIndex={-1}>
          <button
            className={`fullscreen-close ${deletingUser ? 'pt-65' : null}`}
            onClick={closeDeleteUserModal}
            disabled={deletingUser}
          >
            <img src={backIcon} width={42} height={42} alt="Close icon" />
            {t.close}
          </button>

          <div className="card-wrapper">
            <div className="card">
              <h3>{t.deleteaccountheader}</h3>
              <div className="pt-65">{t.deleteaccounttext}</div>
              <div className="form">
                <div className="pt-65">{t.deleteaccountconfirm}</div>
                <TextInput
                  type="email"
                  labelName={t.deleteaccountemail}
                  id="email"
                  value={email}
                  onChangeFunction={handleTitleChange}
                  required={true}
                  inputmode="email"
                />
              </div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => deleteUserAccount()}
                disabled={deletingUser || !hasEnteredEmail}
              >
                {t.deleteaccount}
              </button>
            </div>
          </div>

          <div className="btn-group-wrapper">
            <div className="btn-group">
              <button
                className="btn btn-secondary w-100"
                onClick={() => closeDeleteUserModal()}
                disabled={deletingUser}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
  ];
};

export default DeleteAccount;
